import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { emit, reconnectWithToken, disconnectSocket } from './socket';
import './App.css';

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await emit('admin:login', { username, password });
      await reconnectWithToken(res.data.access_token);
      onLogin(res.data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Kronix</h1>
          <p>Admin Portal</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
          {error && <div className="error-msg">{error}</div>}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function PinModal({ device, mode, onClose, onSuccess }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const title = mode === 'set' ? 'Set PIN & Activate' : 'Reset PIN';
  const description = mode === 'set'
    ? `Activate device ${device.deviceId} by setting a PIN. The user can then log in with this PIN.`
    : `Reset the PIN for device ${device.deviceId}. The user will need to use the new PIN to log in.`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!pin || pin.length < 4) { setError('PIN must be at least 4 characters.'); return; }
    if (pin !== confirmPin) { setError('PINs do not match.'); return; }
    setLoading(true);
    try {
      if (mode === 'set') {
        await emit('admin:setPin', { deviceId: device.deviceId, pin });
      } else {
        await emit('admin:resetPin', { deviceId: device.deviceId, newPin: pin });
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <p className="modal-desc">{description}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New PIN</label>
            <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Min 4 characters" autoFocus />
          </div>
          <div className="form-group">
            <label>Confirm PIN</label>
            <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} placeholder="Re-enter PIN" />
          </div>
          {error && <div className="error-msg">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : title}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);
  const [pinModal, setPinModal] = useState(null);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const res = await emit('admin:listDevices');
      setDevices(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newDeviceId.trim()) { setError('Device ID required.'); return; }
    try {
      await emit('admin:addDevice', { deviceId: newDeviceId.trim(), label: newLabel.trim() || undefined });
      setNewDeviceId('');
      setNewLabel('');
      setShowAdd(false);
      fetchDevices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (deviceId) => {
    if (!window.confirm(`Remove device ${deviceId}? This will also unregister it.`)) return;
    try {
      await emit('admin:removeDevice', { deviceId });
      fetchDevices();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUnregister = async (deviceId) => {
    if (!window.confirm(`Unregister device ${deviceId}? The user will need to re-register.`)) return;
    try {
      await emit('admin:unregisterDevice', { deviceId });
      fetchDevices();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="page-section">
      <div className="section-header">
        <h2>Devices</h2>
        <button className="btn-accent" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Cancel' : '+ Add Device'}
        </button>
      </div>

      {showAdd && (
        <form className="add-form glass-panel" onSubmit={handleAdd}>
          <div className="form-row">
            <div className="form-group">
              <label>Device ID</label>
              <input value={newDeviceId} onChange={(e) => setNewDeviceId(e.target.value)} placeholder="e.g. KRX-0004" />
            </div>
            <div className="form-group">
              <label>Label (optional)</label>
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Office Unit" />
            </div>
            <button type="submit" className="btn-primary btn-sm">Add</button>
          </div>
        </form>
      )}

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container glass-panel">
          <table>
            <thead>
              <tr>
                <th>Device ID</th>
                <th>Label</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id}>
                  <td className="device-id">{d.deviceId}</td>
                  <td>{d.label || '—'}</td>
                  <td>
                    <span className={`status-badge ${d.registered ? 'status-active' : 'status-available'}`}>
                      {d.registered ? 'Registered' : 'Available'}
                    </span>
                  </td>
                  <td className="date-cell">{new Date(d.createdAt).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    {d.registered ? (
                      <>
                        <button className="btn-ghost btn-pin" onClick={() => setPinModal({ device: d, mode: 'reset' })}>
                          Reset PIN
                        </button>
                        <button className="btn-ghost btn-warning" onClick={() => handleUnregister(d.deviceId)}>
                          Unregister
                        </button>
                      </>
                    ) : (
                      <button className="btn-ghost btn-activate" onClick={() => setPinModal({ device: d, mode: 'set' })}>
                        Set PIN
                      </button>
                    )}
                    <button className="btn-ghost btn-danger" onClick={() => handleRemove(d.deviceId)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {devices.length === 0 && (
                <tr><td colSpan="5" className="empty-row">No devices found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pinModal && (
        <PinModal
          device={pinModal.device}
          mode={pinModal.mode}
          onClose={() => setPinModal(null)}
          onSuccess={() => { setPinModal(null); fetchDevices(); }}
        />
      )}
    </div>
  );
}

function RequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await emit('admin:listVDRequests');
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (id) => {
    try {
      const res = await emit('admin:approveVDRequest', { requestId: id });
      alert(`Approved! Virtual Device ID: ${res.data.assignedDeviceId}`);
      fetchRequests();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    try {
      await emit('admin:rejectVDRequest', { requestId: id });
      fetchRequests();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="page-section">
      <div className="section-header">
        <h2>Virtual Device Requests</h2>
        <button className="btn-ghost" onClick={fetchRequests}>Refresh</button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container glass-panel">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Assigned ID</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="device-id">{r.requesterName}</td>
                  <td>{r.email || '—'}</td>
                  <td className="reason-cell">{r.reason || '—'}</td>
                  <td>
                    <span className={`status-badge status-${r.status}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="device-id">{r.assignedDeviceId || '—'}</td>
                  <td className="date-cell">{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    {r.status === 'pending' && (
                      <>
                        <button className="btn-ghost btn-approve" onClick={() => handleApprove(r.id)}>
                          Approve
                        </button>
                        <button className="btn-ghost btn-danger" onClick={() => handleReject(r.id)}>
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan="7" className="empty-row">No requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    disconnectSocket();
    window.location.href = '/';
  };

  return (
    <div className="dashboard">
      <nav className="sidebar">
        <div className="sidebar-brand">
          <h2>Kronix</h2>
          <span className="badge-admin">Admin</span>
        </div>
        <ul className="nav-list">
          <li><button onClick={() => navigate('/devices')} className="nav-link"><i className="icon">&#9881;</i> Devices</button></li>
          <li><button onClick={() => navigate('/requests')} className="nav-link"><i className="icon">&#9993;</i> Requests</button></li>
        </ul>
        <button className="btn-logout" onClick={handleLogout}>Logout</button>
      </nav>
      <main className="main-content">
        <Routes>
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="*" element={<Navigate to="/devices" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  const handleLogin = (t) => {
    setToken(t);
  };

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <Dashboard />;
}

export default App;
